import React, { useState, useMemo } from 'react';
import { Save, Calculator, DollarSign, Receipt, CreditCard, Plus, Trash2, Calendar } from 'lucide-react';
import { employees, fuelRates, pumpConfigurations, mockMorningReadings } from '../mockData';

const DailyAccounting = () => {
  const [businessDate, setBusinessDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPumps, setSelectedPumps] = useState([]);
  const [readings, setReadings] = useState({});

  const [collections, setCollections] = useState({
    expenses: '',
    paytm: '',
    creditSales: '',
  });

  const [customFields, setCustomFields] = useState([]);

  // Get pump configuration safely
  const getPumpConfig = (pumpId) => {
    return pumpConfigurations.find(p => p.pumpId === pumpId) || { nozzles: [] };
  };

  const togglePump = (pumpId) => {
    if (selectedPumps.includes(pumpId)) {
      setSelectedPumps(selectedPumps.filter(id => id !== pumpId));
      const newReadings = { ...readings };
      delete newReadings[pumpId];
      setReadings(newReadings);
    } else {
      setSelectedPumps([...selectedPumps, pumpId].sort());
      
      const config = getPumpConfig(pumpId);
      const initialReadings = {};
      
      config.nozzles.forEach(nozzle => {
        // Fetch opening from our morning readings (or previous closing in real app)
        const opening = mockMorningReadings.readings[pumpId]?.[nozzle.id] || 100000;
        initialReadings[nozzle.id] = {
          opening: opening,
          closing: '',
          type: nozzle.type
        };
      });

      setReadings(prev => ({
        ...prev,
        [pumpId]: initialReadings
      }));
    }
  };

  const handleReadingChange = (pumpId, nozzleId, val) => {
    setReadings(prev => ({
      ...prev,
      [pumpId]: {
        ...prev[pumpId],
        [nozzleId]: {
          ...prev[pumpId][nozzleId],
          closing: val
        }
      }
    }));
  };

  const handleCollectionChange = (field, val) => {
    setCollections(prev => ({ ...prev, [field]: val }));
  };

  const addCustomField = () => {
    setCustomFields([...customFields, { id: Date.now(), label: '', amount: '' }]);
  };

  const updateCustomField = (id, field, value) => {
    setCustomFields(customFields.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const removeCustomField = (id) => {
    setCustomFields(customFields.filter(c => c.id !== id));
  };

  // Calculations
  const calculatedSales = useMemo(() => {
    let totalAmount = 0;
    const details = {};

    Object.keys(readings).forEach(pumpId => {
      details[pumpId] = {};
      
      Object.keys(readings[pumpId]).forEach(nozzleId => {
        const data = readings[pumpId][nozzleId];
        const closing = parseFloat(data.closing) || 0;
        
        if (closing > data.opening) {
          const liters = closing - data.opening;
          const rate = fuelRates[data.type] || 0;
          const amount = liters * rate;
          
          details[pumpId][nozzleId] = { liters, amount };
          totalAmount += amount;
        } else {
          details[pumpId][nozzleId] = { liters: 0, amount: 0 };
        }
      });
    });

    return { details, totalAmount };
  }, [readings]);

  const totalDeductions = useMemo(() => {
    const customTotal = customFields.reduce((sum, field) => sum + (parseFloat(field.amount) || 0), 0);
    return (parseFloat(collections.expenses) || 0) +
           (parseFloat(collections.paytm) || 0) +
           (parseFloat(collections.creditSales) || 0) +
           customTotal;
  }, [collections, customFields]);

  const cashToCollect = calculatedSales.totalAmount - totalDeductions;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Daily Sales Accounting</h1>
          <p className="text-slate-500 text-sm mt-1">Record sales against a specific business date.</p>
        </div>
        <div className="flex gap-4 items-center">
          <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 flex items-center gap-2 shadow-sm">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input 
              type="date" 
              className="outline-none text-sm font-medium text-slate-700 bg-transparent"
              value={businessDate}
              onChange={(e) => setBusinessDate(e.target.value)}
            />
          </div>
          <button className="btn-primary flex items-center gap-2">
            <Save className="w-4 h-4" /> Save Accounting
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Selection & Readings */}
        <div className="lg:col-span-2 space-y-6">
          <div className="glass-panel p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">Shift Details</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Employee</label>
                <select 
                  className="input-field max-w-md"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                >
                  <option value="">-- Choose Employee --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Pump(s) Operated</label>
                <div className="flex flex-wrap gap-3">
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <button
                      key={num}
                      onClick={() => togglePump(num)}
                      className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                        selectedPumps.includes(num)
                          ? 'bg-primary-500 border-primary-500 text-white shadow-md'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      Pump {num}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {selectedPumps.map(pumpId => {
            const config = getPumpConfig(pumpId);
            const pumpTotal = config.nozzles.reduce((sum, nozzle) => sum + (calculatedSales.details[pumpId]?.[nozzle.id]?.amount || 0), 0);

            return (
              <div key={pumpId} className="glass-panel p-6 border-l-4 border-l-primary-500">
                <h2 className="text-lg font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2 flex justify-between items-center">
                  <span>Meter Readings - Pump {pumpId}</span>
                  <span className="text-sm font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded">
                    ₹{pumpTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </span>
                </h2>
                
                <div className="space-y-6">
                  {config.nozzles.map(nozzle => (
                    <div key={nozzle.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                          Nozzle {nozzle.id} 
                          <span className={`text-xs px-2 py-1 rounded ${
                            nozzle.type === 'Petrol' ? 'bg-orange-100 text-orange-700' :
                            nozzle.type === 'Diesel' ? 'bg-blue-100 text-blue-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {nozzle.type} (₹{fuelRates[nozzle.type]})
                          </span>
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Opening Reading</label>
                          <input type="text" className="input-field bg-slate-100 text-slate-500" value={readings[pumpId]?.[nozzle.id]?.opening || ''} disabled />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Closing Reading</label>
                          <input 
                            type="number" 
                            className="input-field border-primary-200 focus:border-primary-500" 
                            placeholder="Enter closing..."
                            value={readings[pumpId]?.[nozzle.id]?.closing || ''}
                            onChange={(e) => handleReadingChange(pumpId, nozzle.id, e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Liters Sold</label>
                          <input type="text" className="input-field bg-slate-100 font-semibold" value={calculatedSales.details[pumpId]?.[nozzle.id]?.liters?.toFixed(2) || '0.00'} disabled />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          
          {selectedPumps.length === 0 && (
            <div className="glass-panel p-8 text-center text-slate-500">
              Select one or more pumps above to enter meter readings.
            </div>
          )}
        </div>

        {/* Right Column: Deductions & Summary */}
        <div className="space-y-6">
          <div className="glass-panel p-6 sticky top-24">
            <h2 className="text-lg font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">Collections & Deductions</h2>
            
            <div className="space-y-4 mb-6">
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Expenses</label>
                <div className="relative">
                  <Receipt className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input 
                    type="number" 
                    className="input-field pl-9" 
                    placeholder="0.00"
                    value={collections.expenses}
                    onChange={(e) => handleCollectionChange('expenses', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Paytm / UPI</label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input 
                    type="number" 
                    className="input-field pl-9" 
                    placeholder="0.00"
                    value={collections.paytm}
                    onChange={(e) => handleCollectionChange('paytm', e.target.value)}
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Credit Sales</label>
                <div className="relative">
                  <CreditCard className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input 
                    type="number" 
                    className="input-field pl-9" 
                    placeholder="0.00"
                    value={collections.creditSales}
                    onChange={(e) => handleCollectionChange('creditSales', e.target.value)}
                  />
                </div>
              </div>

              {/* Custom Fields */}
              {customFields.map((field, index) => (
                <div key={field.id} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {index === 0 ? 'Custom Field' : ''}
                    </label>
                    <input 
                      type="text" 
                      className="input-field text-sm" 
                      placeholder="Label (e.g. Deposit)"
                      value={field.label}
                      onChange={(e) => updateCustomField(field.id, 'label', e.target.value)}
                    />
                  </div>
                  <div className="w-1/3">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">₹</span>
                      <input 
                        type="number" 
                        className="input-field pl-6" 
                        placeholder="0"
                        value={field.amount}
                        onChange={(e) => updateCustomField(field.id, 'amount', e.target.value)}
                      />
                    </div>
                  </div>
                  <button 
                    onClick={() => removeCustomField(field.id)}
                    className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    title="Remove custom field"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}

              <button 
                onClick={addCustomField}
                className="w-full py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-xl hover:bg-primary-100 transition-colors border border-primary-100"
              >
                <Plus className="w-4 h-4" /> Add Custom Field
              </button>
            </div>

            {/* Final Calculation Panel */}
            <div className="bg-slate-900 rounded-xl shadow-xl overflow-hidden border border-slate-800 text-white p-5 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500 rounded-full blur-3xl opacity-20 -mr-10 -mt-10 pointer-events-none"></div>
              
              <h2 className="text-base font-bold text-slate-100 mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary-400" /> Final Settlement
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Total Sales</span>
                  <span className="font-semibold">₹{calculatedSales.totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Deductions</span>
                  <span className="font-semibold text-red-400">- ₹{totalDeductions.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div className="h-px bg-slate-700 w-full my-2"></div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-300 font-medium">Cash to Collect</span>
                  <span className="text-xl font-bold text-emerald-400">₹{cashToCollect > 0 ? cashToCollect.toLocaleString(undefined, {minimumFractionDigits: 2}) : '0.00'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DailyAccounting;
